import { useCallback, useEffect, useRef, useState } from 'react';
import { ensureChartTooltipDismiss, hideChartTooltip } from './chartTooltipDismiss';

/**
 * useChartDimensions
 * Observes a container element and computes a target SVG height based on width.
 * Also installs system-wide tooltip dismiss and returns hideTooltip for chart effects.
 *
 * @param {(width:number)=>number} heightFn — given container width, return height
 */
export function useChartDimensions(heightFn) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    ensureChartTooltipDismiss();
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) setDimensions({ width: w, height: heightFn(w) });
      }
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      hideChartTooltip(tooltipRef.current);
    };
  }, [heightFn]);

  const hideTooltip = useCallback(() => {
    hideChartTooltip(tooltipRef.current);
  }, []);

  return { containerRef, svgRef, tooltipRef, dimensions, hideTooltip };
}
